/**
 * "Join clips" (not multicam): clips filmed at different times go end to end on
 * the set's timeline instead of being lined up by sound. Each angle starts
 * when the one before it ends, and a cut to each clip where it starts makes
 * the studio, the editor's player and the rough cut follow them in order.
 */

export interface SequencedAngle {
  angleKey: string;
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs: number | null;
  /** The file's own length, used when there's no trim end. */
  durationMs: number;
}

/** How long an angle runs on the timeline: its trimmed length. */
export const playedLengthMs = (angle: SequencedAngle): number =>
  Math.max(0, (angle.sourceOutMs ?? angle.durationMs) - angle.sourceInMs);

/** Start times that play the angles in the order given, each when the one before it ends. */
export const sequenceStarts = (angles: SequencedAngle[]): Record<string, number> => {
  const starts: Record<string, number> = {};
  let at = 0;
  for (const angle of angles) {
    starts[angle.angleKey] = at;
    at += playedLengthMs(angle);
  }
  return starts;
};

/** True when two or more angles already play one after another (to the millisecond). */
export const playsInSequence = (angles: SequencedAngle[]): boolean => {
  if (angles.length < 2) return false;
  const starts = sequenceStarts(angles);
  return angles.every((angle) => Math.abs(angle.timelineStartMs - starts[angle.angleKey]) <= 1);
};

/** A cut to each clip where it starts. The first plays from 0:00 and needs none. */
export const sequenceCuts = (angles: SequencedAngle[]): Array<{ timelineTimeMs: number; targetAngleKey: string }> => {
  const starts = sequenceStarts(angles);
  return angles
    .slice(1)
    .filter((angle) => playedLengthMs(angle) > 0)
    .map((angle) => ({ timelineTimeMs: starts[angle.angleKey], targetAngleKey: angle.angleKey }));
};
